import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { VersionCheckService } from './version-check.service';

export type NotificationStatus = 'error' | 'info' | 'warning' | 'success';

/** Prefisso degli ID assegnati in locale alle notifiche solo-realtime, che non hanno un ID sul DB. */
const LOCAL_NOTIFICATION_PREFIX = 'local:';

/** Filtro sullo stato di lettura per il recupero delle notifiche. */
export type NotificationReadFilter = 'all' | 'read' | 'unread';

/** Bucket/tab di destinazione. */
export type NotificationTargetBucket = 'personal' | 'admin' | 'entity';

/** Query per il recupero paginato/filtrato delle notifiche (tutti i campi opzionali). */
export interface NotificationQuery {
  status?: NotificationReadFilter;
  target?: NotificationTargetBucket;
  messageType?: NotificationStatus;
  q?: string;
  limit?: number;
  offset?: number;
}

/** Query per i conteggi per tab (senza target/paginazione). */
export interface NotificationCountQuery {
  status?: NotificationReadFilter;
  messageType?: NotificationStatus;
  q?: string;
}

/** Conteggi per tab restituiti dal backend. */
export interface NotificationTabCounts {
  personal: number;
  admin: number;
  entity: number;
}

/** Destinatario di una notifica inviata manualmente da un admin. */
export type NotificationTarget = 'admin' | 'entity' | 'user';

export interface SendNotificationPayload {
  TargetType: NotificationTarget;
  /** Uno o più PublisherID (entity) o UserID (user); omesso/vuoto per 'admin'. */
  TargetIDs?: string[];
  Message: string;
  MessageType: NotificationStatus;
  Refferal?: string;
  sendEmail?: boolean;
}

export interface BackendNotification {
  ID: string;
  EventTimestamp: string;
  Message: string;
  MessageType: NotificationStatus;
  Refferal?: string;
  Managed: boolean;
  ObjectID: string;
  Object?: any;
  UserEmail?: string;
  StatusDescription?: string;
  /** Destinatario della notifica: 'admin' | 'entity' | 'user' (può mancare su righe legacy). */
  TargetType?: NotificationTarget;
  TargetID?: string | null;
  DCR_ID?: string | null;
  /** Nome dell'entity risolto lato backend (join su publishers); presente solo per notifiche entity. */
  EntityName?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationBackendService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private ngZone = inject(NgZone);
  private versionCheckService = inject(VersionCheckService);

  private socket: Socket | null = null;
  private notificationsSubject = new BehaviorSubject<BackendNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  /**
   * Emette l'ID dell'audience a fine estrazione e, sul successo, lo snapshot dei campi cambiati
   * (Object: Status, LastExtraction, NextSchedulationDate, AudienceQualityIndex, BytesBilled,
   * Volumes, IsRunning - vedi extract-audience.ts). Il backend notifica solo quando i volumi sono
   * già su MySQL, quindi lo snapshot basta ad aggiornare la riga senza ricaricare la lista.
   */
  private audienceExtractionCompletedSubject = new Subject<{ audienceId: string; audience: Record<string, any> | null }>();
  /**
   * Emette l'esito quando arriva la notifica di fine estrazione, non solo l'ID: al
   * momento dell'invio (extract.ts) il lock e' gia' stato scritto su MySQL, quindi
   * isExtracting/isExtractionStalled sono gia' noti per certo, senza dover aspettare
   * un reload della lista solo per togliere il grigiore ai bottoni della riga.
   */
  private goalExtractionCompletedSubject = new Subject<{ goalId: string; isExtracting: boolean; isExtractionStalled: boolean; succeeded: boolean }>();
  private audienceUpsertedSubject = new Subject<string>();
  private audienceDeletedSubject = new Subject<string>();
  private publisherUpsertedSubject = new Subject<any>();
  private publisherDeletedSubject = new Subject<string>();

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public audienceExtractionCompleted$ = this.audienceExtractionCompletedSubject.asObservable();
  /**
   * Emette {goalId, isExtracting, isExtractionStalled, succeeded} a estrazione conclusa.
   * `succeeded` distingue le due varianti dell'evento: solo sul successo ha senso avviare
   * un polling per i dati (TNCIDs/Events/trend) che potrebbero arrivare a breve.
   */
  public goalExtractionCompleted$ = this.goalExtractionCompletedSubject.asObservable();
  /** Emits the audienceID when an audience is created or updated by any user in the same room. */
  public audienceUpserted$ = this.audienceUpsertedSubject.asObservable();
  /** Emits the audienceID when an audience is deleted by any user in the same room. */
  public audienceDeleted$ = this.audienceDeletedSubject.asObservable();
  /** Emits the full publisher object when a publisher is created or updated. Admin-only. */
  public publisherUpserted$ = this.publisherUpsertedSubject.asObservable();
  /** Emits the publisherID when a publisher is deleted (e.g. BQ rollback). Admin-only. */
  public publisherDeleted$ = this.publisherDeletedSubject.asObservable();

  private apiUrl = environment.apiUrl;
  private connected = false;

  constructor() {
    // setActiveDcr in AuthService can emit currentUser$ multiple times in quick succession
    // (intermediate state, TNC token update, final normalized session). Dedup on the fields
    // that actually require an SSE reconnect / notifications reload: identity + entity scope.
    this.authService.currentUser$
      .pipe(
        map(user => user ? { id: user.ID, entityID: user.entityID } : null),
        distinctUntilChanged((a, b) => a?.id === b?.id && a?.entityID === b?.entityID)
      )
      .subscribe(snapshot => {
        if (snapshot) {
          this.disconnectSse();
          this.connect();
          this.loadNotifications();
        } else {
          this.disconnect();
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Socket.IO — temporaneamente disabilitato per testare SSE in isolamento.
  // Riabilitare rimuovendo i commenti quando SSE sarà stabile.
  // ---------------------------------------------------------------------------
  private connect(): void {
    // [SSE TEST] Socket.IO disabilitato temporaneamente.
    // Il subscriber di currentUser$ chiama disconnectSocketIo() + connect() ad ogni
    // cambio utente/entity, garantendo che la room venga sempre aggiornata.
    //
    // private connectSocketIo(): void {
    //   const user = this.authService.currentUserValue;
    //   if (!user) return;
    //   const token = localStorage.getItem('auth_token');
    //   if (!token) { console.error('[Socket.IO] No auth token'); return; }
    //   this.socket = io(this.apiUrl, { transports: ['websocket', 'polling'], withCredentials: true, auth: { token: `Bearer ${token}` } });
    //   this.socket.on('connect', () => {
    //     this.connected = true;
    //     const room = user.hasGlobalDataAccess ? 'admin-room' : `publisher-room-${user.entityID}`;
    //     this.socket?.emit('join-room', room);
    //   });
    //   this.socket.on('disconnect', () => { this.connected = false; });
    //   this.socket.on('notification', (n: BackendNotification) => { this.ngZone.run(() => this.handleNewNotification(n)); });
    //   this.socket.on('connect_error', (err) => { console.error('[Socket.IO] error:', err); });
    // }
    //
    // private disconnectSocketIo(): void {
    //   if (this.socket) { this.socket.disconnect(); this.socket = null; this.connected = false; }
    // }
    //
    // Nel subscriber currentUser$:
    //   this.disconnectSocketIo();
    //   this.connectSocketIo();

    this.connectSse();
  }

  private disconnect(): void {
    // Socket.IO
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
    // SSE
    this.disconnectSse();
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  // ---------------------------------------------------------------------------
  // SSE
  // ---------------------------------------------------------------------------
  private eventSource: EventSource | null = null;
  private sseReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SSE_RECONNECT_DELAY_MS = 5_000;

  private connectSse(): void {
    // disconnectSse() viene sempre chiamato prima di connectSse() dal subscriber
    // di currentUser$, quindi this.eventSource è già null qui.
    // La guardia rimane come safety net per chiamate dirette.
    if (this.eventSource) return;

    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.error('[SSE] No auth token found');
      return;
    }

    const baseUrl = environment.apiUrl.replace(/\/+$/, '');
    const url = `${baseUrl}/webapp/notification-center/stream?token=${encodeURIComponent(token)}`;

    this.eventSource = new EventSource(url);

    this.eventSource.addEventListener('connected', () => {
      console.log('[SSE] Connected');
      this.connected = true;
    });

    this.eventSource.addEventListener('notification', (event: MessageEvent) => {
      try {
        const notification: BackendNotification = JSON.parse(event.data);
        this.ngZone.run(() => this.handleNewNotification(notification));
      } catch (err) {
        console.error('[SSE] Failed to parse notification:', err);
      }
    });

    // Deploy appena andato in produzione: Cloud Build ha chiamato il backend, che ha fatto
    // broadcast a tutti. L'evento non porta il numero di versione, dice solo "ricontrolla
    // ora": il confronto resta quello di VersionCheckService su version.json, quindi il
    // deploy dell'altra app (Engine/Console, stesso origin) non produce falsi toast.
    this.eventSource.addEventListener('app.version', () => {
      this.ngZone.run(() => this.versionCheckService.checkNow());
    });

    // Flush privileges: il backend chiede di rinnovare il JWT coi permessi aggiornati.
    this.eventSource.addEventListener('privileges.flush', () => {
      this.ngZone.run(() => this.authService.refreshPrivileges());
    });

    this.eventSource.onerror = () => {
      console.warn('[SSE] Connection lost, reconnecting in', this.SSE_RECONNECT_DELAY_MS, 'ms');
      this.connected = false;
      this.disconnectSse();
      this.sseReconnectTimer = setTimeout(() => this.connectSse(), this.SSE_RECONNECT_DELAY_MS);
    };
  }

  private disconnectSse(): void {
    if (this.sseReconnectTimer !== null) {
      clearTimeout(this.sseReconnectTimer);
      this.sseReconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private handleNewNotification(notification: BackendNotification): void {
    // Silent notifications (empty message) only trigger data-sync events,
    // they are not stored in DB and must not appear in the notification center.
    if (notification.Message) {
      // Le notifiche solo-realtime (sendNotification con saveNotification=false lato API) arrivano
      // senza ID perché non sono salvate sul DB: gli do un ID locale, così la campanella le
      // distingue tra loro e setReadStateAndUpdate sa che vanno gestite solo in memoria.
      if (!notification.ID) {
        notification.ID = `${LOCAL_NOTIFICATION_PREFIX}${crypto.randomUUID()}`;
      }
      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = [notification, ...currentNotifications];
      this.notificationsSubject.next(updatedNotifications);
      const unreadCount = updatedNotifications.filter(n => !n.Managed).length;
      this.unreadCountSubject.next(unreadCount);
      this.notificationService.notify(notification.MessageType, notification.Message);
    }

    if (!notification.ObjectID) return;

    switch (notification.StatusDescription) {
      case 'Goal extraction completed':
      case 'Goal extraction failed':
        this.goalExtractionCompletedSubject.next({
          goalId: notification.ObjectID,
          // Object porta il lock gia' sciolto (vedi extract.ts): fallback difensivo a
          // "false" se un vecchio backend non lo valorizzasse ancora.
          isExtracting: notification.Object?.IsExtracting ?? false,
          isExtractionStalled: notification.Object?.IsExtractionStalled ?? false,
          succeeded: notification.StatusDescription === 'Goal extraction completed'
        });
        break;
      case 'Audience extraction completed':
      case 'Audience extraction failed':
        this.audienceExtractionCompletedSubject.next({
          audienceId: notification.ObjectID,
          audience: notification.StatusDescription === 'Audience extraction completed' ? (notification.Object ?? null) : null
        });
        break;
      case 'Audience created':
      case 'Audience updated':
        this.audienceUpsertedSubject.next(notification.ObjectID);
        break;
      case 'Audience deleted':
        this.audienceDeletedSubject.next(notification.ObjectID);
        break;
      case 'Publisher created':
      case 'Publisher updated':
        if (notification.Object) {
          this.publisherUpsertedSubject.next(notification.Object);
          this.authService.upsertDcr(notification.Object);
        }
        break;
      case 'Publisher deleted':
        this.publisherDeletedSubject.next(notification.ObjectID);
        this.authService.removeDcr(notification.ObjectID);
        break;
    }
  }

  /**
   * Load notifications from backend API
   */
  /**
   * Recupera le notifiche filtrate e paginate lato server SENZA toccare lo stream
   * condiviso (`notifications$`/`unreadCount$`): pensato per la pagina /notifications
   * (non interferisce con la campanella). Il backend ordina già per data desc.
   */
  public fetchNotifications(query: NotificationQuery = {}): Observable<BackendNotification[]> {
    return this.http
      .get<{ payload: BackendNotification[] }>(
        `${this.apiUrl}/webapp/notification-center/get-notifications`,
        { params: this.toHttpParams(query) }
      )
      .pipe(map(response => response.payload ?? []));
  }

  /**
   * Conteggi per tab (personal/admin/entity) rispettando status/messageType/ricerca.
   * Alimenta i badge dei tab nella pagina /notifications.
   */
  public fetchNotificationCounts(query: NotificationCountQuery = {}): Observable<NotificationTabCounts> {
    return this.http
      .get<{ payload: NotificationTabCounts }>(
        `${this.apiUrl}/webapp/notification-center/notification-counts`,
        { params: this.toHttpParams(query) }
      )
      .pipe(map(response => response.payload ?? { personal: 0, admin: 0, entity: 0 }));
  }

  // Converte la query in parametri HTTP, omettendo i campi non valorizzati.
  private toHttpParams(query: NotificationQuery | NotificationCountQuery): Record<string, string> {
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params[key] = String(value);
    }
    return params;
  }

  public loadNotifications(): void {
    this.http.get<{ payload: BackendNotification[] }>(`${this.apiUrl}/webapp/notification-center/get-notifications`)
      .subscribe({
        next: (response) => {
          if (response.payload) {
            // Ordinamento difensivo: dalla più recente alla più vecchia,
            // indipendentemente dall'ordine restituito dal backend.
            const sorted = [...response.payload].sort(
              (a, b) => new Date(b.EventTimestamp).getTime() - new Date(a.EventTimestamp).getTime()
            );
            this.notificationsSubject.next(sorted);
            const unreadCount = sorted.filter(n => !n.Managed).length;
            this.unreadCountSubject.next(unreadCount);
          }
        },
        error: (error) => {
          console.error('Error loading notifications:', error);
        }
      });
  }

  /**
   * Imposta lo stato di lettura di una o più notifiche. Firma unica: sempre un
   * array, anche per il singolo click nella campanella. Il backend risponde con
   * gli ID che hanno effettivamente il nuovo stato (scarta i non visibili).
   */
  public setReadState(notificationIds: string[], read: boolean): Observable<{ payload: { IDs: string[] } }> {
    return this.http.put<{ payload: { IDs: string[] } }>(
      `${this.apiUrl}/webapp/notification-center/manage-notify`,
      { ids: notificationIds, managed: read }
    );
  }

  /**
   * Cambia lo stato di lettura e allinea lo stato locale (lista campanella +
   * badge). Allinea solo gli ID confermati dal backend.
   *
   * Le notifiche con ID locale (solo-realtime, vedi handleNewNotification) non esistono sul DB:
   * il loro stato cambia solo in memoria, senza chiamare l'API.
   */
  public setReadStateAndUpdate(notificationIds: string[], read: boolean): Observable<string[]> {
    const localIds = notificationIds.filter(id => id?.startsWith(LOCAL_NOTIFICATION_PREFIX));
    const persistedIds = notificationIds.filter(id => id && !id.startsWith(LOCAL_NOTIFICATION_PREFIX));
    this.applyReadState(localIds, read);

    // `of` e non un Subject già completato: chi si iscrive dopo il return riceve comunque il valore.
    if (persistedIds.length === 0) {
      return of(localIds);
    }

    const result = new Subject<string[]>();
    this.setReadState(persistedIds, read).subscribe({
      next: res => {
        const changed = res?.payload?.IDs ?? [];
        this.applyReadState(changed, read);

        // La lista della campanella contiene solo le non lette: riportando una
        // notifica a "non letta" va RIAGGIUNTA, e il map locale non può farlo
        // (la riga non c'è). Rifetch della lista, che riallinea anche il badge.
        // Il rifetch fa sparire le notifiche locali: non sono sul DB.
        if (!read) this.loadNotifications();

        result.next([...localIds, ...changed]);
        result.complete();
      },
      error: (error) => {
        console.error('Error updating notification read state:', error);
        result.error(error);
      }
    });

    return result.asObservable();
  }

  // Allinea lista campanella e badge per gli ID indicati.
  private applyReadState(ids: string[], read: boolean): void {
    if (ids.length === 0) return;
    const target = new Set(ids);
    const updated = this.notificationsSubject.value.map(n =>
      target.has(n.ID) ? { ...n, Managed: read } : n
    );
    this.notificationsSubject.next(updated);
    this.unreadCountSubject.next(updated.filter(n => !n.Managed).length);
  }

  /**
   * Get unread notifications count
   */
  public getUnreadCount(): Observable<{ payload: number }> {
    return this.http.get<{ payload: number }>(`${this.apiUrl}/webapp/notification-center/count-notifications`);
  }

  /**
   * Invia una notifica manuale verso admin / uno specifico publisher / un singolo
   * utente. Richiede il permesso special_features.can_broadcast lato backend.
   */
  public sendNotification(payload: SendNotificationPayload): Observable<{ payload: { IDs: string[] } }> {
    return this.http.post<{ payload: { IDs: string[] } }>(
      `${this.apiUrl}/webapp/notification-center/send`,
      payload
    );
  }
}
